import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gamepad2 } from 'lucide-react'
import useAudioManager from '../hooks/useAudioManager'
import useAppStore from '../store/useAppStore'
import { getGameModeNavigationItems } from '../config/navigationConfig'
import { hapticImpact } from '../utils/haptic'

// Привязка игровых режимов к карточкам-изображениям
const GAME_CARD_IMAGES = {
  cases: '/images/games/cards/cases.webp',
  battles: '/images/games/cards/battle cases2.webp',
  double: '/images/games/cards/double.webp',
  x50: '/images/games/cards/x50.webp',
  upgrader: '/images/games/cards/upgrade.webp',
  contracts: '/images/games/cards/contract.webp',
  battleRoyale: '/images/games/cards/battle royale.webp',
  crash: '/images/games/cards/crash2.webp',
  mines: '/images/games/cards/mines.webp',
  slot: '/images/games/cards/slots.webp',
  market: '/images/games/cards/market.webp'
}

// Карточки отображаются сразу, без последовательной анимации появления

const Games = ({ embedded = false }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { playSound } = useAudioManager({
    select: 'select'
  })

  // Прокручиваем страницу наверх при монтировании компонента
  useEffect(() => {
    if (embedded) return
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }, [embedded])

  const user = useAppStore((state) => state.user)
  const gamesModes = getGameModeNavigationItems(t, user).filter((mode) => GAME_CARD_IMAGES[mode.id])

  return (
    <div className="text-white p-4">
      {/* Header */}
      <div className="mb-6 hidden">
        <h1 className="text-3xl font-black italic text-accent-primary mb-2 flex items-center gap-3 justify-center uppercase">
          <Gamepad2 size={32} />
          {t('games.title')}
        </h1>
      </div>

      {/* Сетка карточек: 2 колонки на мобиле, 5 на десктопе */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
        {gamesModes.map((mode) => {
          const isDisabled = !mode.available

          return (
            <button
              key={mode.id}
              onClick={() => {
                if (isDisabled) return
                playSound('select')
                hapticImpact('light')
                navigate(mode.path)
              }}
              disabled={isDisabled}
              className={`
                block relative overflow-hidden rounded-2xl aspect-[3/4]
                shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]
                ${isDisabled
                  ? 'border-2 border-white/10 cursor-not-allowed opacity-60'
                  : `border-2 ${mode.borderColor} transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]`}
              `}
            >
              {/* Карточка-изображение режима (недоступные — в ч/б) */}
              <img
                src={GAME_CARD_IMAGES[mode.id]}
                alt={mode.title}
                loading="lazy"
                className={`absolute inset-0 w-full h-full object-cover ${isDisabled ? 'grayscale' : ''}`}
              />

              {/* Градиентная подложка под название снизу */}
              <div className="hidden pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

              {/* Бейджи в верхнем правом углу */}
              {isDisabled && (
                <div className="absolute top-3 right-3 px-3 py-1 bg-black/60 border border-white/15 rounded-full">
                  <span className="text-xs font-semibold text-gray-300">
                    {t('games.comingSoon')}
                  </span>
                </div>
              )}

              {!isDisabled && mode.isNew && (
                <div className={`absolute top-3 right-3 px-3 py-1 ${mode.badgeBg} border ${mode.badgeBorder} rounded-full`}>
                  <span className={`text-xs font-semibold ${mode.textColor}`}>
                    {t('games.new')}
                  </span>
                </div>
              )}

              {/* Название */}
              <div className="absolute inset-x-0 bottom-0 p-3 lg:p-4 flex flex-col items-center gap-2">
                <h2 className="text-base lg:text-xl not-italic font-black uppercase text-center">
                  {mode.title}
                </h2>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default Games
